import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, MessageCircle, RefreshCw, UserPlus, CalendarPlus, Trash2 } from 'lucide-react';
import {
  convertWaitlistEntryToPatient,
  deleteWaitlistEntry,
  fetchWaitlist,
  updateWaitlistEntry,
  WAITLIST_AGE_LABELS,
  WAITLIST_PERIOD_LABELS,
  WAITLIST_STATUS_LABELS,
  type WaitlistEntry,
  type WaitlistStatus,
} from '../services/bookingApi';
import { describeError } from '../utils/describeError';

const STATUS_TONE: Record<WaitlistStatus, string> = {
  PENDENTE: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
  EM_CONTATO: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300',
  AGENDADO: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
  DESISTIU: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

const waLink = (phone: string) => `https://wa.me/${(phone.replace(/\D/g, '').length <= 11 ? '55' : '') + phone.replace(/\D/g, '')}`;

/**
 * Bloco #64: aba "Lista de Espera / Férias" — interessados represados durante
 * o recesso, com filtros, status e ações rápidas (cadastrar paciente / agendar).
 */
export const Waitlist: React.FC<{
  professionals?: { id: string; name: string }[];
  defaultProfessionalId?: string;
  onPatientCreated?: () => void;
  onSchedule?: (entry: WaitlistEntry) => void;
}> = ({ professionals = [], defaultProfessionalId = '', onPatientCreated, onSchedule }) => {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | WaitlistStatus>('');
  const [age, setAge] = useState('');
  const [period, setPeriod] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [toDelete, setToDelete] = useState<WaitlistEntry | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await fetchWaitlist());
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const rows = useMemo(
    () =>
      entries.filter((e) => {
        if (status && e.status !== status) return false;
        if (age && e.ageGroup !== age) return false;
        if (period && (e.preferredPeriod || '') !== period) return false;
        const day = e.createdAt.slice(0, 10);
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
      }),
    [entries, status, age, period, from, to],
  );

  const changeStatus = async (entry: WaitlistEntry, next: WaitlistStatus) => {
    setBusyId(entry.id);
    try {
      const updated = await updateWaitlistEntry(entry.id, { status: next });
      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    } catch (err) {
      window.alert(`Não foi possível alterar o status: ${describeError(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (entry: WaitlistEntry) => {
    if (!window.confirm(`Criar o cadastro de paciente para ${entry.name}?`)) return;
    setBusyId(entry.id);
    try {
      const r = await convertWaitlistEntryToPatient(entry.id, professionalId || undefined);
      setEntries((prev) => prev.map((e) => (e.id === r.entry.id ? r.entry : e)));
      onPatientCreated?.();
      window.alert(r.created ? 'Paciente cadastrado. Complete a ficha em Pacientes.' : 'Esta inscrição já tem cadastro de paciente.');
    } catch (err) {
      window.alert(`Não foi possível cadastrar: ${describeError(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  /** Bloco #71: só tira da lista depois que a API confirmou (não reaparece ao recarregar). */
  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusyId(toDelete.id);
    setDeleteError(null);
    try {
      await deleteWaitlistEntry(toDelete.id);
      setEntries((prev) => prev.filter((e) => e.id !== toDelete.id));
      setNotice(`${toDelete.name} foi excluído(a) da lista de espera.`);
      setToDelete(null);
    } catch (err) {
      setDeleteError(`Não foi possível excluir: ${describeError(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const select = 'px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-bold dark:text-white outline-none';
  const fmtDate = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-16">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black dark:text-white tracking-tight flex items-center gap-3">
            <CalendarClock className="text-amber-500" size={28} /> Lista de Espera / Férias
          </h1>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">Interessados inscritos pelo link externo durante o recesso</p>
        </div>
        <button onClick={() => void load()} className="px-5 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-5 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">De</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`block ${select}`} /></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Até</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`block ${select}`} /></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Turno</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className={`block ${select}`}><option value="">Todos</option>{Object.entries(WAITLIST_PERIOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Faixa etária</label>
          <select value={age} onChange={(e) => setAge(e.target.value)} className={`block ${select}`}><option value="">Todas</option>{Object.entries(WAITLIST_AGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as WaitlistStatus | '')} className={`block ${select}`}><option value="">Todos</option>{Object.entries(WAITLIST_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        {professionals.length > 0 && (
          <div className="space-y-1 ml-auto"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Cadastrar pacientes para</label>
            <select value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} className={`block ${select}`}><option value="">Sem profissional definido</option>{professionals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        )}
      </div>

      {notice && (
        <div role="status" className="px-5 py-4 rounded-2xl text-xs font-bold bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 flex justify-between gap-4">
          {notice}
          <button onClick={() => setNotice(null)} className="text-[10px] font-black uppercase tracking-widest">Fechar</button>
        </div>
      )}

      {error && <div role="alert" className="px-5 py-4 rounded-2xl text-xs font-bold bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300">Não foi possível carregar a lista de espera: {error}</div>}

      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-5 sm:p-10 text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando...</p>
        ) : rows.length === 0 ? (
          <p className="p-5 sm:p-10 text-sm text-slate-400 italic text-center">Nenhum inscrito para os filtros escolhidos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                  <th className="px-4 py-4">Inscrição</th>
                  <th className="px-4 py-4">Nome</th>
                  <th className="px-4 py-4">WhatsApp</th>
                  <th className="px-4 py-4">Faixa etária</th>
                  <th className="px-4 py-4">Turno</th>
                  <th className="px-4 py-4">Queixa resumida</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-4 text-xs font-bold text-slate-500 whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                    <td className="px-4 py-4 text-sm font-black text-slate-800 dark:text-white">{e.name}{e.patientId && <span className="block text-[9px] font-black uppercase text-emerald-600">paciente cadastrado</span>}</td>
                    <td className="px-4 py-4 text-xs font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      <a href={waLink(e.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-600 hover:underline" title="Conversa rápida no WhatsApp">
                        <MessageCircle size={14} /> {e.phone}
                      </a>
                    </td>
                    <td className="px-4 py-4 text-xs font-bold text-slate-600 dark:text-slate-300">{WAITLIST_AGE_LABELS[e.ageGroup] ?? e.ageGroup}</td>
                    <td className="px-4 py-4 text-xs font-bold text-slate-600 dark:text-slate-300">{e.preferredPeriod ? WAITLIST_PERIOD_LABELS[e.preferredPeriod] : '—'}</td>
                    <td className="px-4 py-4 text-xs text-slate-500 dark:text-slate-400 font-medium max-w-[260px] truncate" title={e.reason}>{e.reason}</td>
                    <td className="px-4 py-4">
                      <select
                        value={e.status}
                        disabled={busyId === e.id}
                        onChange={(ev) => void changeStatus(e, ev.target.value as WaitlistStatus)}
                        className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border-0 outline-none cursor-pointer ${STATUS_TONE[e.status]}`}
                        aria-label={`Status de ${e.name}`}
                      >
                        {Object.entries(WAITLIST_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <button onClick={() => void convert(e)} disabled={busyId === e.id || !!e.patientId} title="Converter em cadastro de paciente" className="p-2.5 mr-2 bg-indigo-50 dark:bg-indigo-950/20 text-indigo-500 hover:bg-indigo-500 hover:text-white rounded-xl transition-all disabled:opacity-40">
                        <UserPlus size={14} />
                      </button>
                      <button onClick={() => onSchedule?.(e)} disabled={busyId === e.id} title="Criar agendamento" className="p-2.5 mr-2 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl transition-all disabled:opacity-40">
                        <CalendarPlus size={14} />
                      </button>
                      <button onClick={() => { setDeleteError(null); setToDelete(e); }} disabled={busyId === e.id} title="Excluir da lista" aria-label={`Excluir ${e.name} da lista`} className="p-2.5 bg-rose-50 dark:bg-rose-950/20 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toDelete && (
        <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="wl-del-title">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl p-5 sm:p-8 space-y-5">
            <h3 id="wl-del-title" className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Excluir da lista de espera?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <strong>{toDelete.name}</strong> será removido(a) da lista. Esta ação não poderá ser desfeita.
              {toDelete.patientId && <span className="block mt-2 text-xs text-slate-500">O cadastro de paciente e os agendamentos já criados continuam.</span>}
            </p>
            {deleteError && <div role="alert" className="px-4 py-3 rounded-2xl text-xs font-bold bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300">{deleteError}</div>}
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setToDelete(null)} disabled={busyId === toDelete.id} className="px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">Cancelar</button>
              <button type="button" onClick={() => void confirmDelete()} disabled={busyId === toDelete.id} className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
                {busyId === toDelete.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Waitlist;
